import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "./Header";
import Footer from "./Footer";

import {
  fetchPaymentHistory,
  selectPayments,
  selectPaymentsStatus,
  selectPaymentsError,
  selectPaymentsMeta,
  setPaymentsPage,
} from "../redux/slices/paymentsSlice";

function Orders() {
  const dispatch = useDispatch();

  const items = useSelector(selectPayments);
  const status = useSelector(selectPaymentsStatus);
  const errorMsg = useSelector(selectPaymentsError);
  const meta = useSelector(selectPaymentsMeta);

  const loading = status === "loading";

  // API returns { page, limit, total, payments: [...] } — derive totalPages here
  const totalPages = Math.max(
    1,
    Math.ceil((meta.total || 0) / (meta.limit || 10)),
  );

  useEffect(() => {
    dispatch(fetchPaymentHistory({ page: meta.page, limit: meta.limit }));
  }, [dispatch, meta.page, meta.limit]);

  const goPrev = () => dispatch(setPaymentsPage(Math.max(1, meta.page - 1)));
  const goNext = () =>
    dispatch(setPaymentsPage(Math.min(totalPages, meta.page + 1)));

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    // dd/mm/yyyy
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const getPaymentId = (p) => p?.paymentId || p?.id || p?._id || "-";
  const getTxnId = (p) => p?.razorpayPaymentId || "-";
  const getStatus = (p) => p?.status || "-";

  const formatMoney = (amount, currency = "INR") => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return amount ?? "-";
    try {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      return String(n);
    }
  };

  const formatAddress = (addr) => {
    if (!addr) return "";
    return [addr.addressLine1, addr.addressLine2, addr.zipcode]
      .filter(Boolean)
      .join(", ");
  };

  return (
    <>
      <Header />

      <main className="pt-3 pb-2">
        <section className="container">
          <div className="row pt-2">
            <div className="d-flex justify-content-between mb-3">
              <div className="w-100">
                <span className="text-uppercase text-secondary fw-semibold fs-6 ls-md">
                  ORDER HISTORY
                </span>
                <h2 className="fw-semibold ls-sm text-success fs-3 lh-1">
                  Product Order's History
                </h2>
                <div className="mt-n3 pt-2">
                  <img src="/assets/images/line-shap.webp" width="150" alt="" />
                </div>
              </div>
            </div>
          </div>

          {errorMsg ? (
            <div className="alert alert-danger py-2">{errorMsg}</div>
          ) : null}

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <div
                className="spinner-border"
                role="status"
                aria-hidden="true"
              />
            </div>
          ) : null}

          {!loading && !items.length ? (
            <div className="card rounded-4 border border-light shadow-sm">
              <div className="card-body p-5">
                <h5 className="lh-1 mb-0">No Payment history found</h5>
                <p className="text-muted mb-0">
                  Your payments will appear here.
                </p>
              </div>
            </div>
          ) : null}

          <div className="row g-2 align-items-stretch">
            {!loading &&
              items.map((p, idx) => {
                const lineItems = Array.isArray(p?.items) ? p.items : [];
                const tax = p?.taxBreakup;

                return (
                  <div
                    className="col-lg-4 col-md-6 col-12"
                    key={getPaymentId(p) + idx}
                  >
                    <div className="bg-light p-4 h-100 rounded-4">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <span className="text-primary d-block text-uppercase fw-semibold ls-lg fs-6">
                          {formatDate(p?.paidAt)}
                        </span>
                        <span
                          className={`badge rounded-3 px-3 text-capitalize ${
                            getStatus(p) === "success"
                              ? "bg-success"
                              : "bg-secondary"
                          }`}
                        >
                          {getStatus(p)}
                        </span>
                      </div>

                      {lineItems.map((li, liIdx) => (
                        <div
                          key={li.productId + liIdx}
                          className="d-flex justify-content-between border-bottom pb-2 mb-2"
                        >
                          <div>
                            <h6 className="mb-0 lh-sm fw-semibold">
                              {li?.productSnapshot?.name || "Product"}
                            </h6>
                            <span className="text-muted small text-capitalize">
                              {li?.productSnapshot?.category}
                            </span>
                            <div className="text-dark small">
                              Qty &mdash; {li.quantity} &times; ₹{li.price}
                            </div>
                          </div>
                          <div className="text-end fw-semibold text-dark">
                            ₹{li.total}
                          </div>
                        </div>
                      ))}

                      <div className="d-flex justify-content-between text-dark small">
                        <span>Subtotal</span>
                        <span>{formatMoney(p?.subtotal, p?.currency)}</span>
                      </div>

                      <div className="d-flex justify-content-between text-dark small">
                        <span>Shipping</span>
                        <span>
                          {p?.shippingCharge
                            ? formatMoney(p.shippingCharge, p?.currency)
                            : "Free"}
                        </span>
                      </div>

                      {tax?.gstIncluded ? (
                        <div className="d-flex justify-content-between text-muted small">
                          <span>GST ({tax.gstRate}%)</span>
                          <span>{formatMoney(tax.gstAmount, p?.currency)}</span>
                        </div>
                      ) : null}

                      <hr className="my-2" />

                      <div className="d-flex justify-content-between fw-semibold text-dark mb-2">
                        <span>Total Payment</span>
                        <span>{formatMoney(p?.totalAmount, p?.currency)}</span>
                      </div>

                      <p className="fs-6 text-dark mb-0 lh-sm ls-sm">
                        Payment ID &mdash; {getPaymentId(p)}
                        <br />
                        Txn. ID &mdash; {getTxnId(p)}
                        {formatAddress(p?.shippingAddress) ? (
                          <>
                            <br />
                            Delivered To &mdash;{" "}
                            {formatAddress(p?.shippingAddress)}
                          </>
                        ) : null}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>

          {!loading && items.length ? (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={goPrev}
                disabled={meta.page <= 1}
              >
                Prev
              </button>
              <span className="text-dark small">
                Page {meta.page} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={goNext}
                disabled={meta.page >= totalPages}
              >
                Next
              </button>
            </div>
          ) : null}
        </section>
      </main>

      <Footer />
    </>
  );
}

export default Orders;
